import React from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Button, Chip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import CorporateEventsService, { RegistrationCustomQuestion } from '~/services/CorporateEventsService';

interface RegistrationCustomQuestionsListProps {
  eventId?: string;
  questions: Array<RegistrationCustomQuestion>;
  onQuestionsChange: (questions: Array<RegistrationCustomQuestion>) => void;
  onEditClick: (question: RegistrationCustomQuestion) => void;
  onCreateClick: () => void;
}

export default function RegistrationCustomQuestionsList({
  eventId,
  questions = [],
  onQuestionsChange,
  onEditClick,
  onCreateClick,
}: RegistrationCustomQuestionsListProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 25,
    page: 0,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['corporate-events', 'registration-custom-questions', eventId],
    queryFn: () => CorporateEventsService.getRegistrationCustomQuestionsByEventId(eventId),
    enabled: !!eventId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => CorporateEventsService.deleteRegistrationCustomQuestion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'registration-custom-questions', eventId] });
      enqueueSnackbar('Question deleted successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error deleting question: ${error.message}`, { variant: 'error' });
    },
  });

  const handleDeleteClick = (question: RegistrationCustomQuestion) => {
    if (
      !window.confirm(`Are you sure you want to delete the question "${question.label}"? This action cannot be undone.`)
    ) {
      return;
    }

    if (!eventId || !question.id) {
      const updatedQuestions = questions.filter((q) => q.id !== question.id);
      onQuestionsChange(updatedQuestions);
      return;
    }
    deleteMutation.mutate(question.id);
  };

  const columns: Array<GridColDef<RegistrationCustomQuestion>> = [
    {
      field: 'label',
      headerName: 'Label',
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'fieldType',
      headerName: 'Field Type',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'isRequired',
      headerName: 'Required',
      width: 100,
      renderCell: (params: GridRenderCellParams<RegistrationCustomQuestion>) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.isRequired ? <Chip label="Yes" color="primary" size="small" /> : <Chip label="No" size="small" />}
        </Box>
      ),
    },
    {
      field: 'sortOrder',
      headerName: 'Sort Order',
      width: 150,
      minWidth: 150,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 130,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEditClick(params.row)}
          color="inherit"
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteClick(params.row)}
          color="error"
        />,
      ],
    },
  ];

  const allQuestions = eventId ? data || [] : questions;

  return (
    <Box>
      {!!error && <Alert severity="error">{error?.message}</Alert>}
      <Box mb={4} display="flex" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateClick}>
          Add Question
        </Button>
      </Box>
      <DataGrid
        loading={isLoading && !!eventId}
        rows={allQuestions}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row: RegistrationCustomQuestion) => row.id || `temp-${row.label}-${row.fieldName}`}
        paginationMode="client"
        rowCount={allQuestions.length}
        slots={{
          pagination: GridPagination,
        }}
        autoHeight
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    </Box>
  );
}
